import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Plus } from "lucide-react";
import { toast } from "sonner";

const FREQ = ["ad_evento", "mensile", "trimestrale", "semestrale", "annuale"];
const STATUS = ["da_eseguire", "in_corso", "eseguito", "scaduto", "non_applicabile"];
const ACTION_STATUS = ["aperta", "in_corso", "completata", "annullata"];
const PRIORITY = ["basso", "medio", "alto", "critico"];

export default function Controls() {
  const [tab, setTab] = useState("controls");
  const [controls, setControls] = useState([]);
  const [tests, setTests] = useState([]);
  const [actions, setActions] = useState([]);
  const [risks, setRisks] = useState([]);
  const [newCtrl, setNewCtrl] = useState({ org_risk_id: "", description: "", policy_ref: "", frequency: "mensile", is_preventive: true, is_automatic: false, evidence_type: "" });
  const [newTest, setNewTest] = useState({ control_id: "", test_type: "ToD", outcome: "non_testato", findings: "", corrective_actions: "" });
  const [newAction, setNewAction] = useState({ title: "", description: "", status: "aperta", priority: "medio", due_date: "" });
  const [openAddCtrl, setOpenAddCtrl] = useState(false);
  const [openAddTest, setOpenAddTest] = useState(false);
  const [openAddAct, setOpenAddAct] = useState(false);

  const load = async () => {
    const [{ data: c }, { data: t }, { data: a }, { data: r }] = await Promise.all([
      api.get("/controls"), api.get("/control-tests"), api.get("/corrective-actions"), api.get("/risks?only_active=true"),
    ]);
    setControls(c); setTests(t); setActions(a); setRisks(r);
  };
  useEffect(() => { load(); }, []);

  const addControl = async () => {
    if (!newCtrl.description) return toast.error("Descrizione richiesta");
    if (!newCtrl.org_risk_id && risks[0]) newCtrl.org_risk_id = risks[0].id;
    try {
      await api.post("/controls", newCtrl);
      toast.success("Controllo creato");
      setOpenAddCtrl(false); setNewCtrl({ org_risk_id: "", description: "", policy_ref: "", frequency: "mensile", is_preventive: true, is_automatic: false, evidence_type: "" });
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const addTest = async () => {
    if (!newTest.control_id && controls[0]) newTest.control_id = controls[0].id;
    if (!newTest.control_id) return toast.error("Nessun controllo");
    try {
      await api.post("/control-tests", { ...newTest, executed_at: new Date().toISOString() });
      toast.success("Test registrato");
      setOpenAddTest(false); setNewTest({ control_id: "", test_type: "ToD", outcome: "non_testato", findings: "", corrective_actions: "" });
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const addAction = async () => {
    if (!newAction.title) return toast.error("Titolo richiesto");
    try {
      await api.post("/corrective-actions", newAction);
      toast.success("Azione creata");
      setOpenAddAct(false); setNewAction({ title: "", description: "", status: "aperta", priority: "medio", due_date: "" });
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const moveAction = async (a, status) => {
    await api.put(`/corrective-actions/${a.id}`, { status });
    load();
  };

  const updateDesign = async (c, design_evaluation) => {
    await api.put(`/controls/${c.id}`, { design_evaluation });
    load();
  };

  const analyzeCtrl = async (c) => {
    try {
      const { data } = await api.post("/ai/analyze-control-design", { control_id: c.id });
      toast.success("Analisi AI completata", { description: JSON.stringify(data.parsed).slice(0, 100) });
    } catch (e) { toast.error("Errore AI"); }
  };

  const lvlBadge = (lvl) => <Badge className={`badge-${lvl || "non_valutato"} rounded-sm border`} variant="outline">{lvl || "non val."}</Badge>;

  return (
    <div className="space-y-6" data-testid="controls-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tag">Controlli</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1">Controlli & Test</h1>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="controls" className="rounded-sm" data-testid="tab-controls">Controlli Livello I</TabsTrigger>
          <TabsTrigger value="tests" className="rounded-sm" data-testid="tab-tests">Test Livello II</TabsTrigger>
          <TabsTrigger value="actions" className="rounded-sm" data-testid="tab-actions">Azioni correttive</TabsTrigger>
        </TabsList>

        <TabsContent value="controls" className="mt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={openAddCtrl} onOpenChange={setOpenAddCtrl}>
              <DialogTrigger asChild><Button className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="add-control-btn"><Plus size={14} className="mr-1" /> Nuovo controllo</Button></DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle>Nuovo controllo</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Select value={newCtrl.org_risk_id} onValueChange={(v) => setNewCtrl((s) => ({ ...s, org_risk_id: v }))}>
                    <SelectTrigger className="rounded-sm"><SelectValue placeholder="Rischio associato" /></SelectTrigger>
                    <SelectContent>{risks.map((r) => <SelectItem key={r.id} value={r.id}>{r.catalog?.ade_code} — {(r.catalog?.description || "").slice(0, 50)}</SelectItem>)}</SelectContent>
                  </Select>
                  <Textarea placeholder="Descrizione del controllo" value={newCtrl.description} onChange={(e) => setNewCtrl((s) => ({ ...s, description: e.target.value }))} className="rounded-sm" data-testid="ctrl-desc" />
                  <Input placeholder="Riferimento policy" value={newCtrl.policy_ref} onChange={(e) => setNewCtrl((s) => ({ ...s, policy_ref: e.target.value }))} className="rounded-sm" />
                  <Select value={newCtrl.frequency} onValueChange={(v) => setNewCtrl((s) => ({ ...s, frequency: v }))}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{FREQ.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input placeholder="Tipo evidenza attesa" value={newCtrl.evidence_type} onChange={(e) => setNewCtrl((s) => ({ ...s, evidence_type: e.target.value }))} className="rounded-sm" />
                </div>
                <DialogFooter><Button onClick={addControl} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="save-control">Crea</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="card-flat overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="py-3 px-3 text-left">Descrizione</th>
                  <th className="py-3 px-3 text-left">Frequenza</th>
                  <th className="py-3 px-3 text-left">Stato</th>
                  <th className="py-3 px-3 text-left">Design</th>
                  <th className="py-3 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {controls.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-500">Nessun controllo. Crea il primo controllo dal pulsante in alto.</td></tr>}
                {controls.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100" data-testid={`control-row-${c.id}`}>
                    <td className="py-3 px-3 max-w-md truncate text-slate-800" title={c.description}>{c.description}</td>
                    <td className="py-3 px-3 text-slate-600 data-num">{c.frequency}</td>
                    <td className="py-3 px-3"><Badge variant="outline" className="rounded-sm">{c.status}</Badge></td>
                    <td className="py-3 px-3">
                      <Select value={c.design_evaluation || "non_valutato"} onValueChange={(v) => updateDesign(c, v)}>
                        <SelectTrigger className="h-8 w-36 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="adeguato">Adeguato</SelectItem>
                          <SelectItem value="parzialmente">Parzialmente</SelectItem>
                          <SelectItem value="inadeguato">Inadeguato</SelectItem>
                          <SelectItem value="non_valutato">Non valutato</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-3">
                      <Button size="sm" variant="outline" onClick={() => analyzeCtrl(c)} className="rounded-sm text-xs"><Sparkles size={12} className="mr-1" /> AI Design</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="tests" className="mt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={openAddTest} onOpenChange={setOpenAddTest}>
              <DialogTrigger asChild><Button className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="add-test-btn"><Plus size={14} className="mr-1" /> Nuovo test</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuovo test L2</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Select value={newTest.control_id} onValueChange={(v) => setNewTest((s) => ({ ...s, control_id: v }))}>
                    <SelectTrigger className="rounded-sm"><SelectValue placeholder="Controllo" /></SelectTrigger>
                    <SelectContent>{controls.map((c) => <SelectItem key={c.id} value={c.id}>{c.description.slice(0, 60)}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={newTest.test_type} onValueChange={(v) => setNewTest((s) => ({ ...s, test_type: v }))}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="ToD">ToD - Design</SelectItem><SelectItem value="ToE">ToE - Effectiveness</SelectItem></SelectContent>
                  </Select>
                  <Select value={newTest.outcome} onValueChange={(v) => setNewTest((s) => ({ ...s, outcome: v }))}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="efficace">Efficace</SelectItem>
                      <SelectItem value="parzialmente">Parzialmente</SelectItem>
                      <SelectItem value="non_efficace">Non efficace</SelectItem>
                      <SelectItem value="non_testato">Non testato</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea placeholder="Findings" value={newTest.findings} onChange={(e) => setNewTest((s) => ({ ...s, findings: e.target.value }))} className="rounded-sm" />
                </div>
                <DialogFooter><Button onClick={addTest} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="save-test">Registra</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="card-flat overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b text-xs uppercase tracking-wider text-slate-600"><tr><th className="py-3 px-3 text-left">Tipo</th><th className="py-3 px-3 text-left">Outcome</th><th className="py-3 px-3 text-left">Data</th><th className="py-3 px-3 text-left">Findings</th></tr></thead>
              <tbody>
                {tests.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-slate-500">Nessun test registrato</td></tr>}
                {tests.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100">
                    <td className="py-3 px-3"><Badge variant="outline" className="rounded-sm">{t.test_type}</Badge></td>
                    <td className="py-3 px-3"><Badge variant="outline" className="rounded-sm">{t.outcome}</Badge></td>
                    <td className="py-3 px-3 text-xs data-num text-slate-500">{t.executed_at?.slice(0, 10)}</td>
                    <td className="py-3 px-3 text-slate-700 max-w-md truncate">{t.findings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="actions" className="mt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={openAddAct} onOpenChange={setOpenAddAct}>
              <DialogTrigger asChild><Button className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="add-action-btn"><Plus size={14} className="mr-1" /> Nuova azione</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuova azione correttiva</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Input placeholder="Titolo" value={newAction.title} onChange={(e) => setNewAction((s) => ({ ...s, title: e.target.value }))} className="rounded-sm" />
                  <Textarea placeholder="Descrizione" value={newAction.description} onChange={(e) => setNewAction((s) => ({ ...s, description: e.target.value }))} className="rounded-sm" />
                  <Select value={newAction.priority} onValueChange={(v) => setNewAction((s) => ({ ...s, priority: v }))}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITY.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="date" value={newAction.due_date} onChange={(e) => setNewAction((s) => ({ ...s, due_date: e.target.value }))} className="rounded-sm" />
                </div>
                <DialogFooter><Button onClick={addAction} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="save-action">Crea</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-4 gap-4" data-testid="kanban-board">
            {ACTION_STATUS.map((st) => (
              <div key={st} className="bg-slate-50 border border-slate-200 rounded-sm p-3 min-h-[280px]">
                <div className="label-tag mb-3">{st}</div>
                <div className="space-y-2">
                  {actions.filter((a) => a.status === st).map((a) => (
                    <div key={a.id} className="card-flat p-3 text-sm" data-testid={`action-${a.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="font-medium text-slate-900">{a.title}</div>
                        {lvlBadge(a.priority)}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 data-num">{a.due_date}</div>
                      <div className="text-xs text-slate-600 mt-2">{a.description}</div>
                      <div className="flex gap-1 mt-2">
                        {ACTION_STATUS.filter((s) => s !== st).map((s) => (
                          <Button key={s} size="sm" variant="outline" className="rounded-sm text-[10px] h-6" onClick={() => moveAction(a, s)}>{s}</Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
